import React, { useRef, useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Download, Share2, Twitter, Instagram, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const PowerScoreShareCard = ({ 
  userName, 
  totalScore, 
  academicScore, 
  tacticalScore, 
  brandScore,
  major,
  cohort = "Winter '26",
  isParent = false  // New prop to determine parent vs athlete
}) => {
  const canvasRef = useRef(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Theme colors based on user type
  const themeColor = isParent ? '#A855F7' : '#F97316';  // Purple for parent, Orange for athlete
  const themeColorRGB = isParent ? '168,85,247' : '249,115,22';

  // Generate the share card image
  const generateCard = async (aspectRatio = '1:1') => {
    setIsGenerating(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set dimensions based on aspect ratio
    let width, height;
    if (aspectRatio === '9:16') {
      width = 1080;
      height = 1920;
    } else {
      width = 1080;
      height = 1080;
    }
    
    canvas.width = width;
    canvas.height = height;
    
    // Background - Black
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, width, height);
    
    // Draw topographic grid with theme color
    drawTopographicGrid(ctx, width, height, themeColorRGB);
    
    // Draw radial gradient overlay
    const gradient = ctx.createRadialGradient(width/2, height/3, 0, width/2, height/3, height/1.2);
    gradient.addColorStop(0, 'rgba(0,0,0,0)');
    gradient.addColorStop(0.5, 'rgba(0,0,0,0.5)');
    gradient.addColorStop(1, 'rgba(0,0,0,0.95)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
    
    // Load and draw logo - MUCH BIGGER for social media prominence
    const logoSrc = isParent ? '/images/scholar-parent-logo-new.png' : '/images/scholar-athlete-logo-new.png';
    try {
      const logo = await loadImage(logoSrc);
      // Significantly increased logo size
      const logoHeight = aspectRatio === '9:16' ? 450 : 320;  // Was 300/200
      const logoWidth = (logo.width / logo.height) * logoHeight;
      const logoX = (width - logoWidth) / 2;
      const logoY = aspectRatio === '9:16' ? 100 : 40;
      ctx.drawImage(logo, logoX, logoY, logoWidth, logoHeight);
    } catch (e) {
      console.log('Logo load failed, continuing without');
    }
    
    // User Name - adjusted position for larger logo
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 56px Arial, sans-serif';
    ctx.textAlign = 'center';
    const nameY = aspectRatio === '9:16' ? 650 : 450;
    ctx.fillText(userName.toUpperCase(), width/2, nameY);
    
    // "POWER SCORE" label (or "EXECUTIVE SCORE" for parents)
    ctx.fillStyle = '#9CA3AF';
    ctx.font = 'bold 28px Arial, sans-serif';
    const labelY = aspectRatio === '9:16' ? 750 : 520;
    ctx.fillText(isParent ? 'EXECUTIVE SCORE' : 'POWER SCORE', width/2, labelY);
    
    // Total Score - Large with theme color
    ctx.fillStyle = themeColor;
    ctx.font = 'bold 180px Arial, sans-serif';
    const scoreY = aspectRatio === '9:16' ? 950 : 680;
    ctx.fillText(totalScore.toString(), width/2, scoreY);
    
    // Category Scores - adjusted positions
    const catY = aspectRatio === '9:16' ? 1100 : 780;
    const catSpacing = 280;
    ctx.font = 'bold 24px Arial, sans-serif';
    
    // Academic
    ctx.fillStyle = '#3B82F6';
    ctx.fillText('ACADEMIC', width/2 - catSpacing, catY);
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 48px Arial, sans-serif';
    ctx.fillText(academicScore + '%', width/2 - catSpacing, catY + 50);
    
    // Tactical (or Strategic for parents)
    ctx.fillStyle = '#22C55E';
    ctx.font = 'bold 24px Arial, sans-serif';
    ctx.fillText(isParent ? 'STRATEGIC' : 'TACTICAL', width/2, catY);
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 48px Arial, sans-serif';
    ctx.fillText(tacticalScore + '%', width/2, catY + 50);
    
    // Brand (or Leadership for parents)
    ctx.fillStyle = themeColor;
    ctx.font = 'bold 24px Arial, sans-serif';
    ctx.fillText(isParent ? 'LEADERSHIP' : 'BRAND', width/2 + catSpacing, catY);
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 48px Arial, sans-serif';
    ctx.fillText(brandScore + '%', width/2 + catSpacing, catY + 50);
    
    // "Officially Cleared" Badge with theme color
    const badgeY = aspectRatio === '9:16' ? 1280 : 900;
    ctx.fillStyle = themeColor;
    roundRect(ctx, width/2 - 200, badgeY - 30, 400, 60, 30);
    ctx.fill();
    ctx.fillStyle = '#000000';
    ctx.font = 'bold 24px Arial, sans-serif';
    ctx.fillText('✓ OFFICIALLY CLEARED', width/2, badgeY + 8);
    
    // Cohort
    ctx.fillStyle = '#6B7280';
    ctx.font = 'bold 20px Arial, sans-serif';
    const cohortY = aspectRatio === '9:16' ? 1380 : 970;
    ctx.fillText(`${cohort.toUpperCase()} COHORT`, width/2, cohortY);
    
    // Major badge at bottom with theme color
    const majorY = aspectRatio === '9:16' ? 1480 : 1020;
    ctx.fillStyle = '#1F2937';
    roundRect(ctx, width/2 - 120, majorY - 20, 240, 40, 20);
    ctx.fill();
    ctx.fillStyle = themeColor;
    ctx.font = 'bold 18px Arial, sans-serif';
    ctx.fillText(major.toUpperCase(), width/2, majorY + 5);
    
    // Website at very bottom
    ctx.fillStyle = '#4B5563';
    ctx.font = '16px Arial, sans-serif';
    const webY = aspectRatio === '9:16' ? height - 80 : height - 40;
    ctx.fillText('scholarathlete.academy', width/2, webY);
    
    // Convert to image URL
    const url = canvas.toDataURL('image/png');
    setImageUrl(url);
    setIsGenerating(false);
    return url;
  };

  // Helper: Draw topographic grid with dynamic color
  const drawTopographicGrid = (ctx, width, height, colorRGB) => {
    ctx.strokeStyle = `rgba(${colorRGB},0.25)`;
    ctx.lineWidth = 1;
    
    // Horizontal lines
    for (let y = 0; y < height; y += 60) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }
    
    // Vertical lines
    for (let x = 0; x < width; x += 60) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }
    
    // Diagonal lines for topographic effect
    ctx.strokeStyle = `rgba(${colorRGB},0.15)`;
    for (let i = -height; i < width + height; i += 80) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i + height, height);
      ctx.stroke();
      
      ctx.beginPath();
      ctx.moveTo(i + height, 0);
      ctx.lineTo(i, height);
      ctx.stroke();
    }
  };

  // Helper: Rounded rectangle
  const roundRect = (ctx, x, y, width, height, radius) => {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  };

  // Helper: Load image
  const loadImage = (src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  };

  // Download image
  const downloadImage = async (aspectRatio = '1:1') => {
    const url = await generateCard(aspectRatio);
    const link = document.createElement('a');
    link.download = `PowerScore_${userName.replace(/\s+/g, '_')}_${aspectRatio.replace(':', 'x')}.png`;
    link.href = url;
    link.click();
    toast.success(`Downloaded ${aspectRatio} image!`);
  };

  // Share via Web Share API
  const shareNative = async () => {
    const url = await generateCard('1:1');
    
    // Convert data URL to blob
    const response = await fetch(url);
    const blob = await response.blob();
    const fileName = isParent ? `ExecutiveScore_${userName}.png` : `PowerScore_${userName}.png`;
    const file = new File([blob], fileName, { type: 'image/png' });
    
    const shareText = isParent 
      ? `I just completed the Executive Mirror at Scholar Parent Academy! My Executive Score is ${totalScore}. Leading the next generation of scholar-athletes. #ScholarParent #ExecutiveMirror`
      : `I just leveled up at Scholar Athlete Academy! My Power Score is ${totalScore}. I'm officially cleared for the ${cohort} Cohort. #ScholarAthlete #TheCombine`;
    
    if (navigator.share && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          title: isParent ? 'My Executive Score' : 'My Power Score',
          text: shareText,
          files: [file]
        });
        toast.success('Shared successfully!');
      } catch (err) {
        if (err.name !== 'AbortError') {
          toast.error('Share failed');
        }
      }
    } else {
      // Fallback - copy to clipboard
      try {
        await navigator.clipboard.writeText(shareText + ' Check it out at scholarathlete.academy');
        toast.success('Text copied to clipboard!');
      } catch {
        toast.error('Share not supported on this device');
      }
    }
  };

  // Share to Twitter/X
  const shareToTwitter = async () => {
    const tweetText = isParent
      ? `I just completed the Executive Mirror at @TheScholarAthleteAcademy. My Executive Score is ${totalScore}. Leading the next generation. #ScholarParent #ExecutiveMirror`
      : `I just leveled up at @TheScholarAthleteAcademy. My Power Score is ${totalScore}. I'm officially cleared for the ${cohort} Cohort. #ScholarAthlete #TheCombine`;
    
    const text = encodeURIComponent(tweetText);
    const url = encodeURIComponent('https://scholarathlete.academy');
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
  };

  // Generate initial card on mount
  useEffect(() => {
    if (userName && totalScore) {
      generateCard('1:1');
    }
  }, [userName, totalScore, isParent]);

  return (
    <div className={`${isParent ? 'bg-purple-900/30 border-purple-800' : 'bg-gray-900/50 border-gray-800'} border rounded-xl p-6`}>
      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
        <Share2 className={`w-5 h-5 ${isParent ? 'text-purple-500' : 'text-orange-500'}`} />
        SHARE YOUR {isParent ? 'EXECUTIVE' : 'POWER'} SCORE
      </h3>
      
      {/* Canvas (hidden) */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      
      {/* Preview */}
      {imageUrl && (
        <div className="mb-6 relative">
          <img 
            src={imageUrl} 
            alt={`${isParent ? 'Executive' : 'Power'} Score Card Preview`}
            className={`w-full max-w-md mx-auto rounded-lg border ${isParent ? 'border-purple-700' : 'border-gray-700'}`}
          />
        </div>
      )}
      
      {isGenerating && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className={`w-8 h-8 animate-spin ${isParent ? 'text-purple-500' : 'text-orange-500'}`} />
        </div>
      )}
      
      {/* Share Buttons */}
      <div className="space-y-4">
        {/* Native Share (Mobile) */}
        <Button
          onClick={shareNative}
          className={`w-full ${isParent ? 'bg-purple-500 hover:bg-purple-600' : 'bg-orange-500 hover:bg-orange-600'} text-black font-bold py-4`}
        >
          <Share2 className="w-5 h-5 mr-2" />
          Share to Instagram Stories / More
        </Button>
        
        {/* Twitter/X */}
        <Button
          onClick={shareToTwitter}
          className="w-full bg-black hover:bg-gray-900 text-white font-bold py-4 border border-gray-700"
        >
          <Twitter className="w-5 h-5 mr-2" />
          Share to X (Twitter)
        </Button>
        
        {/* Download Options */}
        <div className="grid grid-cols-2 gap-3">
          <Button
            onClick={() => downloadImage('1:1')}
            variant="outline"
            className="bg-gray-800 hover:bg-gray-700 text-white border-gray-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Feed (1:1)
          </Button>
          <Button
            onClick={() => downloadImage('9:16')}
            variant="outline"
            className="bg-gray-800 hover:bg-gray-700 text-white border-gray-700"
          >
            <Instagram className="w-4 h-4 mr-2" />
            Stories (9:16)
          </Button>
        </div>
      </div>
      
      <p className="text-gray-500 text-xs text-center mt-4">
        Download your Signing Day card and share your achievement!
      </p>
    </div>
  );
};

export default PowerScoreShareCard;
